import { type ComponentPropsWithoutRef, forwardRef } from "react";

import { Box, Tooltip } from "@mantine/core";

import { IconInfoCircle } from "@tabler/icons-react";

import { CLIENT_FLAG_VISIBILITY_MESSAGE } from "../constants/client-flags";
import {
  VISIBILITY_INFO_ICON_SIZE,
  VISIBILITY_TOOLTIP_CLOSE_DELAY,
  VISIBILITY_TOOLTIP_MAX_WIDTH,
} from "../constants/ui";

interface ClientFlagVisibilityInfoProps {
  /**
   * Render the tooltip in a portal. A popover passes `false`, so a click on the message counts as
   * inside the popover and does not close it.
   */
  withinPortal?: boolean;
}

/**
 * The info icon beside a client flags heading, whose tooltip says flags stay inside the practice.
 *
 * Every surface that lists a client's flags — the info box, the client list popover, and the
 * drawer — draws this one, so the wording cannot drift between them. The icon takes keyboard
 * focus and opens on a tap too, since a hover-only tooltip never shows on a touch screen.
 *
 * The tooltip stays open while the pointer is on it, so a reader can move onto the message to
 * magnify it.
 */
export default function ClientFlagVisibilityInfo({
  withinPortal = true,
}: ClientFlagVisibilityInfoProps) {
  return (
    <Tooltip
      withArrow
      multiline
      withinPortal={withinPortal}
      maw={VISIBILITY_TOOLTIP_MAX_WIDTH}
      label={CLIENT_FLAG_VISIBILITY_MESSAGE}
      closeDelay={VISIBILITY_TOOLTIP_CLOSE_DELAY}
      styles={{ tooltip: { pointerEvents: "auto" } }}
      events={{ hover: true, focus: true, touch: true }}
    >
      <VisibilityIcon />
    </Tooltip>
  );
}

/**
 * The focusable icon the tooltip opens from.
 *
 * The tooltip sets `aria-describedby` on it only while it is open. While it is set, the icon
 * carries Mantine's stop-propagation marker, so Escape closes the tooltip and not the drawer that
 * holds it, which would drop its staged flags. Once the tooltip is closed, Escape reaches the
 * drawer as usual.
 */
const VisibilityIcon = forwardRef<HTMLSpanElement, ComponentPropsWithoutRef<"span">>(
  function VisibilityIcon(props, ref) {
    const isTooltipOpen = Boolean(props["aria-describedby"]);

    return (
      <Box
        {...props}
        ref={ref}
        c="gray.7"
        role="img"
        tabIndex={0}
        display="flex"
        component="span"
        data-testid="client-flag-visibility-info"
        aria-label={CLIENT_FLAG_VISIBILITY_MESSAGE}
        data-mantine-stop-propagation={isTooltipOpen ? "true" : undefined}
      >
        <IconInfoCircle size={VISIBILITY_INFO_ICON_SIZE} />
      </Box>
    );
  },
);
